import { Button } from '@/common/ui/button'
import { parseAsString, useQueryState } from 'nuqs'
import { useDateFilter } from '@/common/hooks/useDateFilter'
import { useDateRangeStack } from '@/common/hooks/useDateRangeStack'
import {
	startOfDay,
	endOfDay,
	startOfMonth,
	endOfMonth,
	subDays,
	subMonths,
	format,
} from 'date-fns'

const dateRangeOptions = [
	{
		label: 'ทั้งหมด',
		mobileLabel: 'ทั้งหมด',
		value: 'all',
	},
	{
		label: 'วันนี้',
		mobileLabel: 'วันนี้',
		value: 'today',
	},
	{
		label: 'เมื่อวาน',
		mobileLabel: 'เมื่อวาน',
		value: 'yesterday',
	},
	{
		label: '7 วัน',
		mobileLabel: '7วัน',
		value: '7days',
	},
	{
		label: 'เดือนนี้',
		mobileLabel: 'เดือนนี้',
		value: 'thisMonth',
	},
	{
		label: 'เดือนที่แล้ว',
		mobileLabel: 'เดือนก่อน',
		value: 'lastMonth',
	},
]

export default function DateRangeButtons({
	defaultValue = 'today',
}: {
	defaultValue?: string
}) {
	const [dateRange, setDateRange] = useQueryState(
		'dateRange',
		parseAsString.withDefault(defaultValue),
	)
	const { setDateFilter } = useDateFilter()
	const {
		yesterdayStack,
		lastMonthStack,
		resetStacks,
		resetOppositeStack,
		incrementStack,
	} = useDateRangeStack()

	const handleDateRangeChange = (value: string) => {
		setDateRange(value)
		const today = new Date()

		// Reset stacks when selecting non-stackable options
		if (value !== 'yesterday' && value !== 'lastMonth') {
			resetStacks()
		}
		// Clear opposite stack when switching between stackable options
		else if (value === 'yesterday') {
			resetOppositeStack('yesterday')
		} else if (value === 'lastMonth') {
			resetOppositeStack('lastMonth')
		}

		switch (value) {
			case 'all': {
				setDateFilter({
					startDate: '',
					endDate: '',
				})
				break
			}
			case 'today': {
				setDateFilter({
					startDate: format(startOfDay(today), 'yyyy-MM-dd'),
					endDate: format(endOfDay(today), 'yyyy-MM-dd'),
				})
				break
			}
			case 'yesterday': {
				// Increment stack and calculate the date
				incrementStack('yesterday')
				const newStack = yesterdayStack + 1
				const targetDate = subDays(today, newStack)
				setDateFilter({
					startDate: format(startOfDay(targetDate), 'yyyy-MM-dd'),
					endDate: format(endOfDay(targetDate), 'yyyy-MM-dd'),
				})
				break
			}
			case '7days': {
				const start = subDays(today, 6) // -6 because we want to include today
				setDateFilter({
					startDate: format(startOfDay(start), 'yyyy-MM-dd'),
					endDate: format(endOfDay(today), 'yyyy-MM-dd'),
				})
				break
			}
			case 'thisMonth': {
				setDateFilter({
					startDate: format(startOfMonth(today), 'yyyy-MM-dd'),
					endDate: format(endOfMonth(today), 'yyyy-MM-dd'),
				})
				break
			}
			case 'lastMonth': {
				// Increment stack and calculate the month
				incrementStack('lastMonth')
				const newStack = lastMonthStack + 1
				const targetMonth = subMonths(today, newStack)
				setDateFilter({
					startDate: format(startOfMonth(targetMonth), 'yyyy-MM-dd'),
					endDate: format(endOfMonth(targetMonth), 'yyyy-MM-dd'),
				})
				break
			}
		}
	}

	// Helper function to get display label with stack count
	const getDisplayLabel = (option: {
		label: string
		mobileLabel: string
		value: string
	}) => {
		const baseLabel = {
			desktop: option.label,
			mobile: option.mobileLabel,
		}

		if (option.value === 'yesterday' && yesterdayStack > 0) {
			return {
				desktop: `${baseLabel.desktop} (${yesterdayStack} วันก่อน)`,
				mobile: `${baseLabel.mobile}(${yesterdayStack})`,
			}
		}
		if (option.value === 'lastMonth' && lastMonthStack > 0) {
			return {
				desktop: `${baseLabel.desktop} (${lastMonthStack} เดือนก่อน)`,
				mobile: `${baseLabel.mobile}(${lastMonthStack})`,
			}
		}
		return baseLabel
	}

	return (
		<div className="flex flex-wrap gap-1 sm:gap-2">
			{dateRangeOptions.map((option) => {
				const displayLabel = getDisplayLabel(option)
				return (
					<Button
						key={option.value}
						variant={dateRange === option.value ? 'default' : 'outline'}
						size="sm"
						onClick={() => handleDateRangeChange(option.value)}
						className="h-8 px-2 text-xs sm:h-9 sm:px-3 sm:text-sm"
					>
						{/* Mobile: Shorter labels */}
						<span className="sm:hidden">{displayLabel.mobile}</span>
						{/* Desktop: Full labels */}
						<span className="hidden sm:inline">{displayLabel.desktop}</span>
					</Button>
				)
			})}
		</div>
	)
}
